import { useState } from "react";
import { Download, Trash2, FileText, Film, Music, Image as ImageIcon, Sparkles } from "lucide-react";
import { downloadFile } from "@/lib/api";
import { PrivateImage } from "./PrivateMedia";
import { toast } from "sonner";
import { fmtBytes, fmtDate } from "@/lib/files";
import { cn } from "@/lib/utils";

const KIND_ICON = { image: ImageIcon, video: Film, audio: Music, document: FileText };

export function FileCard({ file, onDelete, onOpen }) {
  const [confirm, setConfirm] = useState(false);
  const Icon = KIND_ICON[file.kind] || FileText;
  const isImage = file.kind === "image";

  const askDelete = (e) => {
    e.stopPropagation();
    if (!confirm) { setConfirm(true); setTimeout(() => setConfirm(false), 3000); return; }
    onDelete?.(file);
  };

  return (
    <div className="jv-panel hud-chamfer hud-bracket group flex flex-col overflow-hidden" data-testid={`file-card-${file.id}`}>
      <button onClick={() => onOpen?.(file)} data-testid={`file-open-${file.id}`}
        className="relative aspect-[4/3] w-full bg-black/40 overflow-hidden flex items-center justify-center border-b border-jarvis-cyan/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-jarvis-cyan">
        {isImage ? (
          <PrivateImage id={file.id} alt={file.prompt || file.name} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        ) : (
          <Icon className="w-9 h-9 text-jarvis-cyan/60" />
        )}
        <div className="pointer-events-none absolute inset-0 jv-grid-lines opacity-20" />
        {file.source === "generated" && (
          <span className="absolute top-2 left-2 flex items-center gap-1 font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 border border-jarvis-purple/50 bg-black/60 text-jarvis-violet">
            <Sparkles className="w-2.5 h-2.5" /> AI
          </span>
        )}
      </button>

      <div className="p-2.5 flex flex-col gap-1.5 min-w-0">
        <div className="font-ui text-[13px] text-slate-100 truncate" title={file.prompt || file.name} data-testid={`file-name-${file.id}`}>
          {file.prompt || file.name}
        </div>
        <div className="flex items-center justify-between font-num text-[10px] text-slate-500">
          <span className="uppercase tracking-wider">{file.kind}</span>
          <span>{fmtBytes(file.size)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="font-num text-[10px] text-slate-600">{fmtDate(file.created_at)}</span>
          <div className="flex items-center gap-1">
            <button onClick={(event) => { event.stopPropagation(); downloadFile(file).catch((failure) => toast.error(failure.message)); }} data-testid={`file-download-${file.id}`}
              className="w-7 h-7 flex items-center justify-center border border-jarvis-cyan/25 text-jarvis-cyan hover:bg-jarvis-cyan/15 transition-colors" title="Download">
              <Download className="w-3.5 h-3.5" />
            </button>
            <button onClick={askDelete} data-testid={`file-delete-${file.id}`} title={confirm ? "Click again to confirm" : "Delete"}
              className={cn("h-7 flex items-center justify-center gap-1 border transition-all px-1.5",
                confirm ? "border-jarvis-red text-jarvis-red bg-jarvis-red/15 font-mono text-[9px] uppercase" : "w-7 border-white/10 text-slate-500 hover:text-jarvis-red hover:border-jarvis-red/40")}>
              <Trash2 className="w-3.5 h-3.5" />{confirm && "sure?"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
